import React from "react";
import { useAtom } from "jotai";
import { legacyCartAtom } from "@/store/legacy-cart.store";
import {
  addToLegacyCart,
  getLegacyCart,
  updateLegacyCartItemQuantity,
} from "@/services/cart.service";
import type { LegacyCartResponse } from "@/types/legacy-cart.types";
import { CartError } from "@/types/legacy-cart.types";
import { getDefaultUserInfo } from "@/types/account.types";
import { userInfoBase } from "@/store/account.store";

interface LegacyCartHook {
  /** The cart or null if not loaded */
  cart: LegacyCartResponse | null;

  /**
   * Refreshes the cart.
   * If you're server-rendering, it's probably better to load the cart atom from `getServerSideProps` than use this function:
   * ```ts
   * // in your getServerSideProps:
   * cart legacyCart = await getLegacyCart();
   * return { legacyCart, ... };
   *
   * // then, in your server render function:
   * const { userInfo, legacyCart, ... } = props;
   * useHydrateAtoms([
   *    [userInfoBase, userInfo],
   *    [legacyCartAtom, legacyCart],
   * ]);
   * ```
   */
  fetchCart: () => Promise<void>;

  /** Adds an item into the user's cart */
  addToCart: (idIQ: number, quantity: number) => Promise<LegacyCartResponse>;

  /** Updates the quantity of an item in the cart */
  updateQuantity: (idIQ: number, quantity: number) => Promise<void>;

  /** Removes an item from the cart (aliases `updateQuantity`) */
  removeFromCart: (idIQ: number) => Promise<void>;

  /** Checks to see if an IdIQ is in the user's cart */
  containsIdIQ: (idIQ: number | null) => boolean;
}

/**
 * Hook for accessing and loading the legacy cart.
 * @returns Returns an object containing the cart and related cart functions
 */
export function useLegacyCart(): LegacyCartHook {
  const [cart, setCart] = useAtom(legacyCartAtom);
  const [, setUserInfo] = useAtom(userInfoBase);

  /** Updates the cart and userinfo stores from a LegacyCartResponse */
  const updateCartFromResponse = React.useCallback(
    (response: LegacyCartResponse) => {
      setCart(response);

      // update userInfo's numItemsInCart to update to the latest qty
      setUserInfo((prev) => ({
        ...(prev ?? getDefaultUserInfo()),
        numItemsInCart: response?.CartTotalItemQuantity ?? 0,
      }));
    },
    [setCart, setUserInfo],
  );

  const fetchCart = React.useCallback(async () => {
    updateCartFromResponse(await getLegacyCart());
  }, [updateCartFromResponse]);

  const containsIdIQ = React.useCallback(
    (idIQ: number | null) => {
      if (cart === null || !idIQ) {
        return false;
      }

      return cart.CartItems.some((item) => item.IdInventoryQuality === idIQ);
    },
    [cart],
  );

  const addToCart = React.useCallback(
    async (idIQ: number, quantity: number = 1) => {
      const newCart = await addToLegacyCart(idIQ, quantity);

      if (newCart.Error) {
        throw new CartError("Failed to add item to cart", newCart.Message);
      }

      updateCartFromResponse(newCart);
      return newCart;
    },
    [updateCartFromResponse],
  );

  const updateQuantity = React.useCallback(
    async (idIQ: number, quantity: number = 0) => {
      const newCart = await updateLegacyCartItemQuantity(idIQ, quantity);

      if (newCart.Error) {
        throw new CartError("Could not update quantity", newCart.Message);
      }

      updateCartFromResponse(newCart);
    },
    [updateCartFromResponse],
  );

  return {
    cart,
    fetchCart,
    addToCart,
    updateQuantity,
    containsIdIQ,
    removeFromCart: (idIQ) => updateQuantity(idIQ, 0),
  };
}
